import { Alert, Button, Card, Form, Input, Result, Typography } from 'antd'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { signup } from '../api'
import type { SaasApiError } from '../api'

interface FormValues {
  email: string
  password: string
  confirm: string
}

/**
 * Phase S1 — public, unauthenticated signup. Deliberately does NOT log
 * the user in on success (the backend never issues a session cookie
 * until `email_verified` is true) — success here always means "verify
 * next", never "you're in now".
 *
 * LOCAL-AUTH-A1: when the backend is APP_ENV=local/development it may
 * include `development_verification_url` so developers can complete the
 * real verify flow without a mailbox. Preview/Production never return
 * that field — this UI only shows the Local block when the API did.
 */
export default function SignUpPage() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const [developmentVerificationUrl, setDevelopmentVerificationUrl] = useState<string | null>(null)

  const onFinish = async (values: FormValues) => {
    setSubmitting(true)
    setError(null)
    try {
      const email = values.email.trim()
      const { data } = await signup(email, values.password)
      setSubmittedEmail(email)
      setDevelopmentVerificationUrl(data.development_verification_url?.trim() || null)
    } catch (err) {
      setError((err as SaasApiError).message || 'Sign up failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
      <Card style={{ width: 420 }}>
        {submittedEmail ? (
          developmentVerificationUrl ? (
            <Result
              status="success"
              title="注册成功"
              subTitle={
                <div style={{ textAlign: 'left' }}>
                  <p>这是本地开发环境，验证邮件不会真实发送。</p>
                  <p>
                    账户 <strong>{submittedEmail}</strong> 已创建。请点击下方按钮完成真实验证流程（
                    <code>/verify/:token</code> → verify API），无需打开 backend terminal。
                  </p>
                  <Typography.Paragraph copyable style={{ marginBottom: 0, wordBreak: 'break-all' }}>
                    {developmentVerificationUrl}
                  </Typography.Paragraph>
                </div>
              }
              extra={[
                <a key="verify" href={developmentVerificationUrl}>
                  <Button type="primary">验证邮箱</Button>
                </a>,
                <Link key="login" to="/login">
                  <Button>返回登录</Button>
                </Link>,
              ]}
            />
          ) : (
            <Result
              status="success"
              title="Check your email"
              subTitle={`We sent a verification link to ${submittedEmail}. Click it to activate your account, then log in.`}
              extra={
                <Link to="/login">
                  <Button type="primary">Back to login</Button>
                </Link>
              }
            />
          )
        ) : (
          <>
            <Typography.Title level={3} style={{ marginBottom: 4 }}>
              Create your Vistoera account
            </Typography.Title>
            <Typography.Text type="secondary">Free while in beta — no card required</Typography.Text>
            <Form layout="vertical" onFinish={onFinish} style={{ marginTop: 24 }} disabled={submitting}>
              {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
              <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email is required' }]}>
                <Input autoFocus autoComplete="username" placeholder="you@example.com" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Password is required' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                ]}
              >
                <Input.Password autoComplete="new-password" placeholder="At least 8 characters" />
              </Form.Item>
              <Form.Item
                name="confirm"
                label="Confirm password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve()
                      return Promise.reject(new Error('Passwords do not match'))
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" placeholder="Re-enter your password" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 8 }}>
                <Button type="primary" htmlType="submit" block loading={submitting}>
                  Create Account
                </Button>
              </Form.Item>
              <Typography.Text type="secondary">
                Already have an account? <Link to="/login">Sign in</Link>
              </Typography.Text>
            </Form>
          </>
        )}
      </Card>
    </div>
  )
}
